import { useEffect, useState } from 'react'
import { setNavStyle } from '@api'

export type DashboardTheme = 'legacy' | 'new'

export const DASHBOARD_THEME_KEY = 'activate.dashboard.theme'
export const THEME_DEFAULT_KEY = 'activate.dashboard.theme.defaulted'
export const THEME_EVENT = 'activate-dashboard-theme'

const NAV_STYLE_SYNCED_KEY = 'activate.dashboard.navStyleSynced'

/** Backend enum names (UserEnums.NavStyle) keyed by the local theme value */
const NAV_STYLE_BY_THEME: Record<DashboardTheme, string> = {
  legacy: 'Legacy',
  new: 'OneClick',
}

export const getDashboardTheme = (): DashboardTheme => {
  try {
    const stored = localStorage.getItem(DASHBOARD_THEME_KEY)
    const hasDefaulted = localStorage.getItem(THEME_DEFAULT_KEY)
    if (!hasDefaulted) {
      localStorage.setItem(THEME_DEFAULT_KEY, 'true')
      if (stored !== 'new') {
        localStorage.setItem(DASHBOARD_THEME_KEY, 'new')
        return 'new'
      }
    }
    return stored === 'legacy' ? 'legacy' : 'new'
  } catch (error) {
    return 'new'
  }
}

/**
 * Nav-style competition telemetry: stamp Preference.NavStyle on the user
 * record. Fire-and-forget — capturing the preference must never block or
 * break navigation.
 */
export const recordNavStyle = (theme: DashboardTheme) => {
  setNavStyle({ NavStyle: NAV_STYLE_BY_THEME[theme] }).catch(() => {
    /* telemetry only */
  })
}

/**
 * Once per tab session, so users who never touch the toggle are still counted
 * with the style they actually use.
 */
export const recordNavStyleOnce = (theme: DashboardTheme) => {
  try {
    if (sessionStorage.getItem(NAV_STYLE_SYNCED_KEY)) return
    sessionStorage.setItem(NAV_STYLE_SYNCED_KEY, 'true')
  } catch (error) {
    /* storage unavailable — record anyway */
  }
  recordNavStyle(theme)
}

/** Single write path for the theme choice: storage, cross-view sync, telemetry. */
export const setDashboardTheme = (theme: DashboardTheme) => {
  try {
    localStorage.setItem(DASHBOARD_THEME_KEY, theme)
    window.dispatchEvent(new Event(THEME_EVENT))
  } catch (error) {
    // ignore storage errors (private mode, disabled storage)
  }
  recordNavStyle(theme)
}

/** The live theme choice: re-renders on the toggle (THEME_EVENT) and on another tab's change (storage). */
export const useDashboardTheme = (): DashboardTheme => {
  const [theme, setTheme] = useState(getDashboardTheme)
  useEffect(() => {
    const syncTheme = () => setTheme(getDashboardTheme())
    window.addEventListener(THEME_EVENT, syncTheme)
    window.addEventListener('storage', syncTheme)
    return () => {
      window.removeEventListener(THEME_EVENT, syncTheme)
      window.removeEventListener('storage', syncTheme)
    }
  }, [])
  return theme
}

export const useIsOneClick = () => useDashboardTheme() === 'new'
